import axios from 'axios';
import Vue from 'vue';
import {vm} from '@/main';
import store from '../../store';
import router from '../../routes/routes';
import {accessRestrictedLocation, moduleForRoute} from '@/routes/guards';
import authService from '@services/auth.service';
import i18n from '@/i18n';

class HttpService {
    constructor() {
        if (HttpService.instance) {
            return HttpService.instance;
        }

        axios.defaults.baseURL = process.env.VUE_APP_API_URL;
        axios.defaults.withCredentials = true;

        axios.interceptors.request.use(
            config => config,
            error => {
                store.commit('STOP_ALL_LOADERS');
                return Promise.reject(error);
            }
        );

        axios.interceptors.response.use(response => {
            if (response && [200, 201, 204].includes(response.status)) {
                return response;
            }
            return Promise.reject(response);
        }, async (error) => {
            store.commit('STOP_ALL_LOADERS');

            if (error.response && error.response.data instanceof Blob) {
                try {
                    error.response.data = JSON.parse(await error.response.data.text());
                } catch (e) { /* non-JSON blob */ }
            }

            if (error.code === 'ERR_NETWORK') {
                Vue.prototype.$toastr.e(i18n.t('Network Error'));
                return Promise.reject(error);
            } else if (error.code === 'ECONNABORTED') {
                /* Can occur on browser redirect */
                return Promise.reject(error);
            } else if (error.code === 'ERR_CANCELED') {
                /* Can occur when another request returns Unauthorized before this one resolves */
                return Promise.reject(error);
            }

            if (error.response) {
                switch (error.response.status) {
                    case 401: {
                        // authService.logout();
                        authService.reloadEnvironment();
                        break;
                    }
                    case 429: {
                        router.replace({'query': null}).catch(() => {});
                        if(authService.isAuthorized) {
                            authService.logout();
                        }
                        break;
                    }
                    case 404:
                    case 403: {
                        if(error.response.data.reload && window.location.pathname !== '/dashboard') {
                            location.replace('/dashboard');
                        }
                        if(error.response.data.module_blocked) {
                            const {reason, module, block_reason} = error.response.data;
                            // Only the view the user is actually on should be torn down.
                            // A background request (autosave, export, modal) for some other
                            // module must not yank them off the page mid-edit.
                            const onBlockedRoute = moduleForRoute(router.currentRoute) === module;
                            // GET_USER_MENU also refreshes the module list.
                            store.dispatch('GET_USER_MENU').then(() => {
                                if (!onBlockedRoute) {
                                    Vue.prototype.$toastr.e(i18n.t('This module is currently unavailable'));
                                    return;
                                }
                                router.replace(accessRestrictedLocation(reason, block_reason)).catch(() => {
                                    // A navigation lock (unsaved changes) can refuse the
                                    // redirect — tell the user rather than failing silently.
                                    Vue.prototype.$toastr.e(i18n.t('This module is currently unavailable'));
                                });
                            });
                        }
                        break;
                    }
                    case 500:
                    case 422: {
                        break;
                    }
                    case 503: {
                        vm.$bvModal.show('maintenance-error-modal');
                        break;
                    }
                    case 417: {
                        // SocialAccount abort - email missing or wrong idcode from HarID
                        location.replace('/auth/login');
                        break;
                    }
                    default: {
                        break;
                    }
                }

                // module_blocked already gave its own feedback (redirect or toast) above.
                if (!error.response.data?.module_blocked) {
                    if (error.response.data) {
                        if (error.response.data.errors) {
                            Object.keys(error.response.data.errors).forEach(key => {
                                Vue.prototype.$toastr.e(error.response.data.errors[key]);
                            });
                        } else if (error.response.data.message) {
                            Vue.prototype.$toastr.e(i18n.t(error.response.data.message));
                        } else {
                            Vue.prototype.$toastr.e(i18n.t('error'));
                        }
                    } else if (error.response.message) {
                        Vue.prototype.$toastr.e(i18n.t(error.message));
                    } else {
                        Vue.prototype.$toastr.e(error);
                    }
                }

            } else if (!error.status) {
                Vue.prototype.$toastr.e(i18n.t('Network Error'));
            }

            return Promise.reject(error);
        });

        axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

        HttpService.instance = this;
    }

    get(url, options) {
        return axios.get(url, options);
    }

    post(url, body, options) {
        return axios.post(url, body, options);
    }

    put(url, body, options) {
        return axios.put(url, body, options);
    }

    delete(url, params) {
        return axios.delete(url, params);
    }

    download(url, options = {}) {
        options.responseType = 'blob';
        return axios.get(url, options);
    }
}

export default new HttpService();
